/**
 * IXD 가이드라인 검증 결과 뷰 — 재사용 가능한 순수 프레젠테이션 컴포넌트.
 *
 * 라이브 결과 페이지(result.tsx)와 다운로드 HTML 뷰어(export/viewer/index.tsx) 양쪽에서
 * 같은 컴포넌트를 사용한다. props(data)만으로 렌더가 완결되므로 스토어 시드가 불필요하다.
 *
 * headerAction 슬롯: 다운로드 버튼 등 헤더 우측 액션을 외부에서 주입한다.
 * 뷰 자체는 filename/useHtmlExport/buildStandaloneHtml 의존을 갖지 않는다
 * (번들 순환 방지 — 뷰어 번들이 이 파일을 통째로 재사용하기 때문).
 */

import {
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
  type MouseEvent,
} from "react";
import { CheckCircle, XCircle, X } from "lucide-react";

import type {
  GuidelineCheckWithPreviewResponse,
  ViolationRef,
} from "@/apis/guideline/types";
import type { IxdChunk } from "@/apis/compare/types";
import {
  IxdPreviewRenderer,
  type IxdPreviewRendererRef,
} from "@/components/ixd-preview";
import { buildDlCellKey, parseDlCellKey } from "@/apis/guideline/dl-cell-key";

// ---------------------------------------------------------------------------
// 빨간 ring 주입 훅 (공유 REVIEW_COUNTERPART_CSS 사용 안 함)
// ---------------------------------------------------------------------------

const VIOLATION_RING_CSS =
  "outline:2px solid #ef4444!important;outline-offset:-2px;position:relative;z-index:10";

function useViolationCellRing(containerId: string, dlCellKeys: string[]): void {
  useEffect(() => {
    const styleElId = `${containerId}-violation-style`;
    let styleEl = document.getElementById(styleElId) as HTMLStyleElement | null;

    if (dlCellKeys.length === 0) {
      styleEl?.remove();
      return;
    }

    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = styleElId;
      document.head.appendChild(styleEl);
    }

    // (DL id, cell id) 단위로 스코프 — 같은 cell_id라도 다른 표면 강조하지 않는다.
    const selectors = dlCellKeys
      .map((key) => {
        const { dlId, cellId } = parseDlCellKey(key);
        return (
          `#${CSS.escape(containerId)} ` +
          `[data-dl-id="${CSS.escape(dlId)}"] ` +
          `[data-cell-id="${CSS.escape(cellId)}"]`
        );
      })
      .join(",");
    styleEl.textContent = `${selectors}{${VIOLATION_RING_CSS}}`;

    return () => {
      document.getElementById(styleElId)?.remove();
    };
  }, [containerId, dlCellKeys]);

  // 언마운트 시 정리 (containerId는 stable이므로 의존성 배열에 포함)
  useEffect(() => {
    return () => {
      document.getElementById(`${containerId}-violation-style`)?.remove();
    };
  }, [containerId]);
}

// ---------------------------------------------------------------------------
// 자체 목차 사이드바 (좌)
// ---------------------------------------------------------------------------

interface TocChunkItem {
  id: string;
  title: string;
  violationCount: number;
}

interface TocCategory {
  label: string;
  chunks: TocChunkItem[];
}

const TocSidebar = ({
  categories,
  activeChunkId,
  onChunkClick,
}: {
  categories: TocCategory[];
  activeChunkId: string | null;
  onChunkClick: (chunkId: string) => void;
}) => (
  <aside className="w-48 shrink-0 overflow-y-auto border-r border-line-normal bg-background-gray-normal">
    {categories.map((cat) => (
      <div
        key={cat.label}
        className="flex flex-col border-b border-line-normal"
      >
        <div className="flex items-center gap-1 border-b border-line-normal bg-background-gray-normal px-4 py-2 caption-1-semibold text-label-normal">
          {cat.label}
        </div>
        <div className="flex flex-col gap-2 px-2 py-2">
          {cat.chunks.map((chunk) => {
            const isActive = activeChunkId === chunk.id;
            return (
              <button
                key={chunk.id}
                type="button"
                onClick={() => onChunkClick(chunk.id)}
                className={
                  "relative flex w-full cursor-pointer items-center gap-1 rounded px-2 py-1 label-2-medium text-left transition-colors " +
                  (isActive
                    ? "bg-primary-base text-static-white"
                    : "text-label-normal hover:bg-neutral-800/8 active:bg-neutral-900/12")
                }
              >
                <span className="flex-1 truncate">{chunk.title}</span>
                {chunk.violationCount > 0 && (
                  <span
                    className="ml-auto shrink-0 w-2 h-2 rounded-full bg-red-500"
                    aria-label="위반 있음"
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>
    ))}
  </aside>
);

// ---------------------------------------------------------------------------
// 위반 상세 패널 (우)
// ---------------------------------------------------------------------------

const ViolationDetailPanel = ({
  cellId,
  violations,
  onClose,
}: {
  cellId: string;
  violations: ViolationRef[];
  onClose: () => void;
}) => (
  <aside className="w-80 shrink-0 overflow-y-auto border-l border-line-normal bg-white">
    <div className="flex items-center justify-between border-b border-line-normal px-4 py-3">
      <span className="label-1-normal-medium text-label-normal">위반 상세</span>
      <button
        type="button"
        title="닫기"
        onClick={onClose}
        className="text-label-assistive hover:text-label-normal transition-colors"
      >
        <X className="size-4" />
      </button>
    </div>
    <div className="px-3 py-1.5 border-b border-line-normal bg-background-gray-normal">
      <p className="caption-2-medium text-label-alternative truncate">
        셀 ID: {cellId}
      </p>
    </div>
    <div className="flex flex-col gap-3 p-3">
      {violations.map((v, i) => (
        <div
          key={i}
          className="rounded-lg border border-red-100 bg-red-50 p-3 space-y-1.5"
        >
          <div className="flex items-start justify-between gap-2">
            <span className="caption-1-semibold text-red-700">
              [{v.ruleId}]
            </span>
          </div>
          {v.title && (
            <p className="caption-1-semibold text-label-normal">{v.title}</p>
          )}
          <p className="caption-2-medium text-red-600 leading-relaxed">
            {v.display}
          </p>
          {v.value != null && (
            <p className="caption-2-medium text-label-alternative">
              값: <code className="font-mono">{v.value}</code>
            </p>
          )}
        </div>
      ))}
    </div>
  </aside>
);

// ---------------------------------------------------------------------------
// 결과 뷰 (public export)
// ---------------------------------------------------------------------------

export interface GuidelineCheckResultViewProps {
  data: GuidelineCheckWithPreviewResponse;
  /** 헤더 우측에 렌더할 액션(다운로드 버튼 등). 다운로드 HTML에선 undefined로 두면 됨. */
  headerAction?: ReactNode;
}

export function GuidelineCheckResultView({
  data,
  headerAction,
}: GuidelineCheckResultViewProps) {
  const rendererRef = useRef<IxdPreviewRendererRef>(null);

  // 위반 tableId 집합 — 렌더 중 파생
  const violationTableIds = new Set<string>(
    data.report.results.flatMap((r) => r.violations).map((v) => v.tableId),
  );

  // 위반 (DL, cell) 합성키 목록 — 렌더 중 파생 (빨간 ring 주입용)
  const violationDlCellKeys = Object.keys(data.violationsByDlCell);

  // 연결+별도 합쳐 단일 스크롤 영역
  const allChunks: IxdChunk[] = [
    ...data.consolidatedChunks,
    ...data.separateChunks,
  ];

  // chunk별 위반 건수: tableId 기준 카운트 (dlView.id ∈ violationTableIds 개수)
  const chunkViolationCount = (chunk: IxdChunk): number =>
    chunk.dlViews.filter((dv) => dv.id != null && violationTableIds.has(dv.id))
      .length;

  // 목차 카테고리 — 렌더 중 파생
  const tocCategories: TocCategory[] = [];
  if (data.consolidatedChunks.length > 0) {
    tocCategories.push({
      label: "연결",
      chunks: data.consolidatedChunks.map((c) => ({
        id: c.id,
        title: c.title,
        violationCount: chunkViolationCount(c),
      })),
    });
  }
  if (data.separateChunks.length > 0) {
    tocCategories.push({
      label: "별도",
      chunks: data.separateChunks.map((c) => ({
        id: c.id,
        title: c.title,
        violationCount: chunkViolationCount(c),
      })),
    });
  }

  // scoped ring 컨테이너 id — useId()로 stable 생성
  const rawId = useId();
  const ringScopeId = `glcheck-${rawId.replace(/[^a-zA-Z0-9_-]/g, "")}`;

  // 빨간 ring 주입
  useViolationCellRing(ringScopeId, violationDlCellKeys);

  // 클릭 선택된 (DL, cell) 합성키
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  // 목차 active chunk
  const [activeChunkId, setActiveChunkId] = useState<string | null>(
    tocCategories[0]?.chunks[0]?.id ?? null,
  );

  const handleChunkClick = (chunkId: string) => {
    setActiveChunkId(chunkId);
    rendererRef.current?.scrollToChunk(chunkId);
  };

  const handlePreviewClick = (e: MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const cell = target.closest("[data-cell-id]") as HTMLElement | null;
    const cellId = cell?.getAttribute("data-cell-id");
    const dlEl = cell?.closest("[data-dl-id]") as HTMLElement | null;
    const dlId = dlEl?.getAttribute("data-dl-id");
    if (!cellId || !dlId) {
      setSelectedKey(null);
      return;
    }
    const key = buildDlCellKey(dlId, cellId);
    const violations = data.violationsByDlCell[key];
    if (violations != null && violations.length > 0) {
      setSelectedKey(key);
    } else {
      setSelectedKey(null);
    }
  };

  // 선택된 (DL, cell)의 위반 목록 — 렌더 중 파생
  const selectedViolations: ViolationRef[] =
    selectedKey != null ? (data.violationsByDlCell[selectedKey] ?? []) : [];

  return (
    <div className="flex h-screen w-full overflow-hidden">
      {/* 목차 (좌) */}
      <TocSidebar
        categories={tocCategories}
        activeChunkId={activeChunkId}
        onChunkClick={handleChunkClick}
      />

      {/* 중앙 메인 */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* 헤더 */}
        <header className="flex h-14 shrink-0 items-center justify-between gap-4 border-b border-line-normal px-5 bg-white">
          <div className="flex items-center gap-3">
            {data.report.passed ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <XCircle className="w-5 h-5 text-red-500" />
            )}
            <span className="label-1-normal-medium text-label-normal">
              {data.report.passed ? "모든 규칙 통과" : "위반 사항 발견"}
            </span>
            <span className="caption-1-semibold text-label-alternative">
              위반 셀 {data.stats.matchedCount}건
            </span>
          </div>
          {headerAction}
        </header>

        {/* 프리뷰 */}
        {allChunks.length > 0 ? (
          <div
            id={ringScopeId}
            className="flex-1 min-h-0 relative"
            onClick={handlePreviewClick}
          >
            <IxdPreviewRenderer
              ref={rendererRef}
              chunks={allChunks}
              showPeriodLabels
            />
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-sm text-label-assistive">
            표시할 프리뷰가 없습니다.
          </div>
        )}
      </main>

      {/* 위반 상세 패널 (우) — 선택된 (DL, cell)이 있을 때만 */}
      {selectedKey != null && selectedViolations.length > 0 && (
        <ViolationDetailPanel
          cellId={parseDlCellKey(selectedKey).cellId}
          violations={selectedViolations}
          onClose={() => setSelectedKey(null)}
        />
      )}
    </div>
  );
}
